import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { MessageSquare, FileText } from "lucide-react-native";
import { CardTitleText, MoonDateField, MoonFormField } from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";

export function VaginalInfectionDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();

  const infection = state.vaginalInfectionHistories[0];

  const updateInfection = (
    updates: Partial<(typeof state.vaginalInfectionHistories)[0]>,
  ) => {
    if (!infection) return;
    updateState({
      vaginalInfectionHistories: [{ ...infection, ...updates }],
    });
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("VaginalInfectionDetails");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t(
        "med_history.gynae.sexual_health.vaginal_infection_details_title",
      )}
      subtitle={t(
        "med_history.gynae.sexual_health.vaginal_infection_details_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        <View style={[GLASS_CARD_STYLE, styles.detailCard]}>
          <CardTitleText>
            {t("med_history.gynae.sexual_health.vaginal_infection_card_title")}
          </CardTitleText>

          <MoonFormField
            label={t(
              "med_history.gynae.sexual_health.vaginal_infection_description_label",
            )}
            labelIcon={FileText}
          >
            <MedicalHistoryTextField
              placeholder={t(
                "med_history.gynae.sexual_health.vaginal_infection_description_placeholder",
              )}
              value={infection?.description ?? ""}
              onChangeText={(text) =>
                updateInfection({ description: text || null })
              }
              multiline
              numberOfLines={3}
              style={{ width: "100%" }}
            />
          </MoonFormField>

          <MoonDateField
            label={t(
              "med_history.gynae.sexual_health.vaginal_infection_start_label",
            )}
            placeholder={t(
              "med_history.gynae.sexual_health.vaginal_infection_start_placeholder",
            )}
            value={infection?.startDate ?? ""}
            onChange={(iso) => updateInfection({ startDate: iso || null })}
          />

          <MoonFormField
            label={t(
              "med_history.gynae.sexual_health.vaginal_infection_notes_label",
            )}
            labelIcon={MessageSquare}
          >
            <MedicalHistoryTextField
              placeholder={t(
                "med_history.gynae.sexual_health.notes_placeholder",
              )}
              value={infection?.notes ?? ""}
              onChangeText={(text) => updateInfection({ notes: text || null })}
              multiline
              numberOfLines={2}
              style={{ width: "100%" }}
            />
          </MoonFormField>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  detailCard: {
    gap: 16,
    padding: 16,
  },
});
